// The share sheet's upload band (DS ui_kits/grackle-app/ShareDialog.jsx, top half) with the
// stages card under it, trimmed to what M1 actually knows. Since SD25 this screen is purely a
// status view: the browser's involvement ended when the upload completed (FR-P3c), so there is
// no client-side work left for it to report on and no reason to keep the tab open. The old
// artboard showed per-rendition progress and "playing at 720p while 1080p finishes" — that's
// FR-P5 (progressive rung attachment), explicitly deferred past M1 (build-plan-m1.md §6): a
// grackle only flips to `ready` once every rung has finished (§2.9), and the §2.11 WS contract
// only ever reports the coarse `uploading|processing|ready|failed` status, never per-rendition
// percentages. So this screen shows the three coarse stages the contract actually has, and
// hands off to the share dialog (then the watch page, WP12) the moment `ready` arrives.
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { GrackleDetail } from "../api/types";
import { progressSocket, type GrackleWsStatus } from "../api/ws";
import { Badge, Card, Icon, IconButton, Logo } from "@grackle/ui";
import { Notice } from "../components/ui";
import DevModeUnlock from "../components/DevModeUnlock";
import PublishStatusPanel from "./PublishStatusPanel";
import ShareDialog from "../watch/ShareDialog";
import { isDevMode, subscribeDevMode } from "../lib/devMode";
import "./recorder.css";

const STAGES: { key: GrackleWsStatus; label: string }[] = [
  { key: "uploading", label: "Uploaded" },
  // SD25: the server publishes — audio track, poster, and segments where a packet copy fits
  // the budget (FR-P11/FR-P12). Nothing is "encoding": the label says what actually happens,
  // and it happens on a worker, which is why the note below no longer asks for a tab.
  { key: "processing", label: "Preparing on the server" },
  { key: "ready", label: "Ready to watch" },
];
const STAGE_ORDER = STAGES.map((s) => s.key);

function stageState(status: GrackleWsStatus | null, stage: GrackleWsStatus): "done" | "active" | "pending" {
  if (!status || status === "failed") return "pending";
  const at = STAGE_ORDER.indexOf(status);
  const of = STAGE_ORDER.indexOf(stage);
  if (of < at) return "done";
  if (of === at) return "active";
  return "pending";
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ProcessingPage() {
  const { grackleId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<GrackleWsStatus | null>(null);
  const [detail, setDetail] = useState<GrackleDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [devMode, setDevMode] = useState(isDevMode());
  useEffect(() => subscribeDevMode(setDevMode), []);

  useEffect(() => {
    if (!grackleId) return;
    let cancelled = false;
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setStatus(d.status);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiFail ? err.message : "Couldn't load this recording.");
      });
    return () => {
      cancelled = true;
    };
  }, [grackleId]);

  useEffect(() => {
    if (!grackleId) return;
    return progressSocket.subscribe(grackleId, (msg) => setStatus(msg.status));
  }, [grackleId]);

  // Reset PR3 (DS 1d): when the recording is ready to watch, its link is ready to send — open
  // the share dialog in its "Your link is ready" state instead of jumping straight to the
  // player. Done (or closing it) goes to the recording, as the old auto-redirect did.
  const [shareOpen, setShareOpen] = useState(false);
  const shareShown = useRef(false);
  useEffect(() => {
    if (status === "ready" && grackleId && !shareShown.current) {
      shareShown.current = true;
      setShareOpen(true);
    }
  }, [status, grackleId]);

  if (!grackleId) {
    return <div className="center-page">Missing recording.</div>;
  }

  const failed = status === "failed";
  const title = detail?.title ?? "Your recording";
  // The upload is complete by the time this page exists (handleStop awaits `finish()`), so
  // the band's bar is full; the status line says what the server is doing now.
  const bandStatus = failed ? "Processing failed" : status === "ready" ? "Ready to watch" : "Uploaded";

  return (
    <div className="rec-shell">
      <div className="rec-topbar">
        <Logo height={22} />
        <DevModeUnlock />
        <div className="spacer" />
        {status === "ready" ? (
          <Link to={`/g/${grackleId}`} className="gk-btn gk-btn--primary gk-btn--sm">
            Open recording
          </Link>
        ) : null}
      </div>

      <div className="rec-body">
        <section className="rec-card" aria-labelledby="proc-title">
          <header className="rec-card__head">
            <Logo variant="mark" height={28} />
            <div className="rec-card__titles">
              <h1 className="rec-card__title" id="proc-title">
                {title}
              </h1>
              <p className="rec-card__sub">
                {failed
                  ? detail?.error ?? "Something went wrong while processing this recording."
                  : "Preparing on the server — sharing opens the moment it is ready to watch."}
              </p>
            </div>
            <IconButton icon="x" label="Close" variant="muted" onClick={() => navigate("/orgs")} />
          </header>

          <div className="rec-band" data-testid="upload-band">
            <div className="rec-band__thumb">
              {detail?.thumbnail_url ? <img src={detail.thumbnail_url} alt="" /> : null}
              {detail?.duration_ms ? (
                <Badge tone="glass" size="sm" className="rec-band__duration">
                  {formatDuration(detail.duration_ms)}
                </Badge>
              ) : null}
            </div>
            <div className="rec-band__body">
              <div className="rec-band__status" role="status">
                {bandStatus}
              </div>
              <div className="rec-band__bar" aria-hidden>
                <span className="rec-band__fill" style={{ width: "100%" }} />
              </div>
              <div className="rec-band__note">
                {failed
                  ? "The upload finished, so nothing was lost."
                  : "The link works right now — it starts playing as soon as the server has prepared it."}
              </div>
            </div>
          </div>

          {loadError ? <Notice tone="error">{loadError}</Notice> : null}

          {failed ? (
            <Card size="sm" className="proc-steps">
              <div className="proc-step">
                <Icon name="warning-circle" size={20} className="proc-step-icon is-failed" />
                <span className="proc-step-name is-active">Processing failed</span>
              </div>
              <div className="proc-note">
                {detail?.error ?? "Try recording again — if this keeps happening, it's worth a bug report."}
              </div>
              <Link to="/record" className="gk-btn gk-btn--secondary gk-btn--block">
                Record again
              </Link>
            </Card>
          ) : (
            <Card size="sm" className="proc-steps">
              <div className="gk-overline">Still processing</div>
              {STAGES.map((stage) => {
                const state = stageState(status, stage.key);
                return (
                  <div className="proc-step" key={stage.key}>
                    <Icon
                      name={state === "done" ? "check-circle" : "circle-notch"}
                      size={18}
                      fill={state === "done"}
                      className={`proc-step-icon${state === "done" ? " is-done" : state === "active" ? " spin is-active" : ""}`}
                    />
                    <span className={`proc-step-name${state === "active" ? " is-active" : ""}`}>{stage.label}</span>
                    <span className="proc-step-state">
                      {state === "done" ? "Done" : state === "active" ? "Working" : "Queued"}
                    </span>
                  </div>
                );
              })}
              <div className="proc-note">
                This finishes on the server — you can close this tab. (Closing it does stop an
                upload still in flight; once the upload is done, nothing here is needed any more.)
              </div>
            </Card>
          )}
        </section>

        {devMode ? <PublishStatusPanel grackleId={grackleId ?? null} /> : null}
      </div>

      {shareOpen ? (
        <ShareDialog
          grackleId={grackleId}
          published
          onClose={() => {
            setShareOpen(false);
            navigate(`/g/${grackleId}`, { replace: true });
          }}
        />
      ) : null}
    </div>
  );
}
